import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSessionData, getProyectos, checkAdmin, getMiOrganizacion } from '../lib/api';
import { useAuth } from './AuthContext';

const ProyectoContext = createContext(null);

const STORAGE_KEYS = {
  proyecto: 'selected_proyecto',
  org: 'active_org_id',
};

export function ProyectoProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [proyectos, setProyectos] = useState([]);
  const [selectedProyecto, setSelectedProyecto] = useState(null);
  const [userRol, setUserRol] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [organizacion, setOrganizacion] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Multi-org state ──────────────────────────────────────────
  const [misOrganizaciones, setMisOrganizaciones] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [showOrgSelector, setShowOrgSelector] = useState(false);

  const fetchProyectos = useCallback(async (orgId = null) => {
    if (!isAuthenticated) {
      setProyectos([]);
      setSelectedProyecto(null);
      setUserRol(null);
      setIsSuperAdmin(false);
      setIsPlatformAdmin(false);
      setOrganizacion(null);
      setMisOrganizaciones([]);
      setActiveOrgId(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Determinar org_id a cargar: parámetro > localStorage > default (backend elige)
      const savedOrgId = localStorage.getItem(STORAGE_KEYS.org);
      const targetOrgId = orgId || (savedOrgId ? parseInt(savedOrgId) : null);

      let proyectosList = [];

      try {
        const response = await getSessionData(targetOrgId);
        const {
          admin,
          organizacion: org,
          proyectos: pList,
          mis_organizaciones: orgs = [],
        } = response.data;

        setIsSuperAdmin(admin.is_super_admin);
        setIsPlatformAdmin(admin.is_platform_admin || false);
        setOrganizacion(org);
        setMisOrganizaciones(orgs);
        proyectosList = pList;

        // Guardar org activa
        const orgActiva = admin.organizacion_id;
        if (orgActiva) {
          setActiveOrgId(orgActiva);
          localStorage.setItem(STORAGE_KEYS.org, orgActiva);
        }

        // Mostrar selector si el usuario tiene acceso a más de una org y no hay preferencia guardada
        if (orgs.length > 1 && !targetOrgId && !savedOrgId) {
          setShowOrgSelector(true);
        }
      } catch (unifiedError) {
        // Fallback a llamadas individuales
        console.warn('Session data endpoint failed, using fallback:', unifiedError);

        try {
          const adminRes = await checkAdmin();
          setIsSuperAdmin(adminRes.data.is_super_admin);
          setIsPlatformAdmin(adminRes.data.is_platform_admin || false);
        } catch {
          setIsSuperAdmin(false);
          setIsPlatformAdmin(false);
        }

        try {
          const orgRes = await getMiOrganizacion();
          setOrganizacion(orgRes.data);
        } catch {
          setOrganizacion(null);
        }

        const proyectosRes = await getProyectos();
        proyectosList = proyectosRes.data;
      }

      setProyectos(proyectosList);

      // Restaurar proyecto seleccionado desde localStorage o seleccionar el primero
      const savedProyectoId = localStorage.getItem(STORAGE_KEYS.proyecto);
      let proyectoToSelect = null;

      if (savedProyectoId) {
        proyectoToSelect = proyectosList.find(p => p.id === parseInt(savedProyectoId));
      }

      if (!proyectoToSelect && proyectosList.length > 0) {
        proyectoToSelect = proyectosList[0];
      }

      if (proyectoToSelect) {
        setSelectedProyecto(proyectoToSelect);
        setUserRol(proyectoToSelect.rol);
        localStorage.setItem(STORAGE_KEYS.proyecto, proyectoToSelect.id);
      }
    } catch (error) {
      console.error('Error fetching session data:', error);
      setProyectos([]);
      setIsSuperAdmin(false);
      setIsPlatformAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchProyectos();
  }, [fetchProyectos]);

  const selectProyecto = async (proyecto) => {
    setSelectedProyecto(proyecto);
    setUserRol(proyecto.rol);
    localStorage.setItem(STORAGE_KEYS.proyecto, proyecto.id);
  };

  /** Cambia la organización activa y recarga el contexto completo. */
  const switchOrganizacion = useCallback(async (orgId) => {
    setShowOrgSelector(false);
    localStorage.setItem(STORAGE_KEYS.org, orgId);
    localStorage.removeItem(STORAGE_KEYS.proyecto); // Limpiar proyecto anterior (es de otra org)
    setSelectedProyecto(null);
    setActiveOrgId(orgId);
    await fetchProyectos(orgId);
  }, [fetchProyectos]);

  const isAdmin = userRol === 'admin' || userRol === 'superadmin' || userRol === 'platform_admin' || isSuperAdmin || isPlatformAdmin;
  const isViewer = userRol === 'viewer' && !isSuperAdmin && !isPlatformAdmin;
  const isVendedor = userRol === 'vendedor';
  const isLider = userRol === 'lider';

  const refreshOrganizacion = useCallback(async () => {
    try {
      const orgRes = await getMiOrganizacion();
      setOrganizacion(orgRes.data);
    } catch {
      setOrganizacion(null);
    }
  }, []);

  const value = {
    proyectos,
    selectedProyecto,
    userRol,
    isAdmin,
    isViewer,
    isVendedor,
    isLider,
    isSuperAdmin,
    isPlatformAdmin,
    organizacion,
    loading,
    selectProyecto,
    refreshProyectos: fetchProyectos,
    refreshOrganizacion,
    // Multi-org
    misOrganizaciones,
    activeOrgId,
    showOrgSelector,
    setShowOrgSelector,
    switchOrganizacion,
  };

  return <ProyectoContext.Provider value={value}>{children}</ProyectoContext.Provider>;
}

export function useProyecto() {
  const context = useContext(ProyectoContext);
  if (!context) {
    throw new Error('useProyecto must be used within a ProyectoProvider');
  }
  return context;
}
